// Campos de las pantallas de entrada (decisión D51): el correo y la contraseña, con el botón para mostrarla u ocultarla.
// Grandes y con la etiqueta arriba, como el resto de la tienda; el error abajo, anunciado.
import { cx, Icono } from '@reparto/ui';
import { type InputHTMLAttributes, useId, useState } from 'react';

export const CLASE_CAMPO =
  'h-14 w-full rounded-2xl border-2 bg-white px-4 text-[18px] text-stone-900 placeholder:text-stone-500 focus:border-primary-600 focus:ring-4 focus:ring-primary-200 focus:outline-none';

type PropsCampo = Omit<InputHTMLAttributes<HTMLInputElement>, 'className'> & {
  etiqueta: string;
  ayuda?: string;
  error?: string | null;
  icono?: string;
};

export function CampoTexto({ etiqueta, ayuda, error, icono, id, ...resto }: PropsCampo) {
  const propio = useId();
  const campo = id ?? propio;
  return (
    <div>
      <label htmlFor={campo} className="block text-base font-semibold text-stone-800">
        {etiqueta}
      </label>
      {ayuda ? <p className="text-[15px] text-stone-600">{ayuda}</p> : null}
      <div className="relative mt-2">
        {icono ? (
          <Icono
            nombre={icono}
            className="pointer-events-none absolute top-1/2 left-4 -translate-y-1/2 text-stone-600"
          />
        ) : null}
        <input
          id={campo}
          {...resto}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${campo}-error` : undefined}
          className={cx(CLASE_CAMPO, icono && 'pl-13', error ? 'border-rose-500' : 'border-stone-300')}
        />
      </div>
      {error ? (
        <p id={`${campo}-error`} role="alert" className="mt-1.5 text-[15px] font-medium text-rose-700">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/** La contraseña con "Mostrar" / "Ocultar" (un botón con su nombre, no solo un ojo). */
export function CampoClave({ etiqueta, ayuda, error, id, ...resto }: Omit<PropsCampo, 'icono' | 'type'>) {
  const propio = useId();
  const campo = id ?? propio;
  const [ver, setVer] = useState(false);
  return (
    <div>
      <label htmlFor={campo} className="block text-base font-semibold text-stone-800">
        {etiqueta}
      </label>
      {ayuda ? <p className="text-[15px] text-stone-600">{ayuda}</p> : null}
      <div className="relative mt-2">
        <Icono nombre="lock" className="pointer-events-none absolute top-1/2 left-4 -translate-y-1/2 text-stone-600" />
        <input
          id={campo}
          {...resto}
          type={ver ? 'text' : 'password'}
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${campo}-error` : undefined}
          className={cx(CLASE_CAMPO, 'pr-14 pl-13', error ? 'border-rose-500' : 'border-stone-300')}
        />
        <button
          type="button"
          onClick={() => setVer((v) => !v)}
          aria-label={ver ? 'Ocultar la contraseña' : 'Mostrar la contraseña'}
          aria-pressed={ver}
          className="absolute top-1/2 right-1.5 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full text-stone-700 hover:bg-stone-100 focus-visible:ring-4 focus-visible:ring-primary-200 focus-visible:outline-none"
        >
          <Icono nombre={ver ? 'visibility_off' : 'visibility'} />
        </button>
      </div>
      {error ? (
        <p id={`${campo}-error`} role="alert" className="mt-1.5 text-[15px] font-medium text-rose-700">
          {error}
        </p>
      ) : null}
    </div>
  );
}
